/**
 * 学员端状态广播器
 *
 * 职责：
 * - 节流序列化 equipmentStore 关键参数，通过 WebSocket 上报给后端
 * - 操作事件（参数调整/步骤提交）即时上报（不节流，但天然低频）
 * - 演练开始/结束时发送 drill:start / drill:end
 *
 * 节流策略：
 * - equipmentStore 状态变化最快 1s 一次（DynamicsEngine tick 是 500ms，但上报频率减半避免洪泛）
 * - 操作事件即时上报（人手操作频率低，每秒不超过 2-3 次）
 *
 * 使用方式：
 *   const broadcaster = new StateBroadcaster(classId);
 *   broadcaster.onDrillStart(sceneId, faultId, faultName, difficulty);
 *   broadcaster.onStateChange(equipmentSnapshot, divergingKeys);
 *   broadcaster.onAction(action, isCorrect);
 *   broadcaster.onDrillEnd(score, grade);
 *   broadcaster.destroy();
 */

import { getRealtimeClient } from './realtime';
import type { ClientMessage } from './realtime-types';

const STATE_MIN_INTERVAL_MS = 1000; // 状态上报最快 1s 一次

export class StateBroadcaster {
  private classId: string;
  private lastStateSentAt = 0;
  private destroyed = false;

  constructor(classId: string) {
    this.classId = classId;
  }

  /** 演练开始：通知后端 */
  onDrillStart(sceneId: string, faultId: string, faultName: string, difficulty: string): void {
    if (this.destroyed) return;
    this.send({
      type: 'drill:start',
      classId: this.classId,
      sceneId,
      faultId,
      faultName,
      difficulty,
    });
  }

  /**
   * 参数状态变化：节流上报
   * @param equipmentSnapshot 设备参数快照 { "eqId:paramId": value, ... }
   * @param divergingKeys 当前发散的参数 key 集合
   */
  onStateChange(equipmentSnapshot: Record<string, number>, divergingKeys: string[]): void {
    if (this.destroyed) return;
    const now = Date.now();
    if (now - this.lastStateSentAt < STATE_MIN_INTERVAL_MS) return; // 节流
    this.lastStateSentAt = now;
    this.send({
      type: 'drill:state',
      classId: this.classId,
      equipmentSnapshot,
      divergingKeys,
    });
  }

  /** 操作事件：即时上报（不节流） */
  onAction(action: string, isCorrect: boolean): void {
    if (this.destroyed) return;
    this.send({
      type: 'drill:action',
      classId: this.classId,
      action,
      isCorrect,
      timestamp: Date.now(),
    });
  }

  /** 演练结束：通知后端 */
  onDrillEnd(score: number, grade: string): void {
    if (this.destroyed) return;
    this.send({
      type: 'drill:end',
      classId: this.classId,
      score,
      grade,
    });
  }

  /** 销毁，停止所有上报 */
  destroy(): void {
    this.destroyed = true;
  }

  // ===== 内部方法 =====

  private send(msg: ClientMessage): void {
    const client = getRealtimeClient();
    if (!client || client.status !== 'connected') return; // 未连接时静默丢弃
    client.send(msg);
  }
}

/**
 * 从 equipmentStore 提取参数快照
 * 返回格式：{ "R-101:reactor_temp": 535, "REG-101:regenerator_temp": 720, ... }
 */
export function extractEquipmentSnapshot(
  equipments: Array<{ id: string; parameters: Array<{ id: string; value: number }> }>,
): Record<string, number> {
  const snapshot: Record<string, number> = {};
  for (const eq of equipments) {
    for (const param of eq.parameters) {
      snapshot[`${eq.id}:${param.id}`] = param.value;
    }
  }
  return snapshot;
}

// ===== 单例管理 =====
let currentBroadcaster: StateBroadcaster | null = null;

/** 获取当前广播器实例（演练开始时创建） */
export function getStateBroadcaster(): StateBroadcaster | null {
  return currentBroadcaster;
}

/** 创建广播器（演练开始时调用） */
export function createStateBroadcaster(classId: string): StateBroadcaster {
  if (currentBroadcaster) currentBroadcaster.destroy();
  currentBroadcaster = new StateBroadcaster(classId);
  return currentBroadcaster;
}

/** 销毁广播器（演练结束时调用） */
export function destroyStateBroadcaster(): void {
  if (currentBroadcaster) {
    currentBroadcaster.destroy();
    currentBroadcaster = null;
  }
}
